/**
 * Confirm dialog shown when a parameter change (dimensions / ceiling height / acoustics /
 * table / mic model / speaker model) would clear the user's manual device edits. The
 * middleware (`calculateDevicesMiddleware`) parks the original action in
 * `ui.pendingParameterChangeAction` and opens the `confirmManualEditReset` modal
 * instead of applying the change.
 *
 * Flow:
 *   - Confirm → re-dispatch the parked action with `meta._confirmedReset: true` so the
 *     middleware lets it through, clear the parked field, close the modal.
 *   - Cancel  → just clear the parked field and close the modal. No state change.
 *
 * The bypass flag rides on `action.meta` rather than being merged into `action.payload`.
 * Some intercepted actions (notably `setSpeakerModel`) take primitive string payloads —
 * mutating the payload to inject the flag would land an object in `area.speakers.model`
 * and break the reducer. `meta` is invisible to reducers, perfect for transport-only
 * markers like this.
 *
 * Built as a smart container around the generic `ConfirmDialog` rather than from
 * scratch so visual styling stays consistent with other confirm dialogs in the app.
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from '@reduxjs/toolkit';
import ConfirmDialog from './ConfirmDialog';
import ReducersIndex from 'reduxModules/ducks';
import { RootState } from 'types/redux/state';

export const CONFIRM_MANUAL_EDIT_RESET_MODAL = 'confirmManualEditReset';

const ManualEditResetDialog: React.FC = () => {
  const dispatch = useDispatch();
  const modals = useSelector((state: RootState) => state.ui.modals);
  const pendingAction = useSelector((state: RootState) => state.ui.pendingParameterChangeAction);

  const isOpen = Boolean(
    modals.find(m => m.modalName === CONFIRM_MANUAL_EDIT_RESET_MODAL)?.modalOpen,
  );

  const cleanup = () => {
    dispatch(ReducersIndex.uiReducers.setPendingParameterChangeAction(null) as unknown as UnknownAction);
    dispatch(ReducersIndex.uiReducers.closeModal(CONFIRM_MANUAL_EDIT_RESET_MODAL) as unknown as UnknownAction);
  };

  const onConfirm = () => {
    // Re-dispatch the parked action with the bypass flag on `meta` so the middleware
    // intercept recognises it as "user already confirmed" and lets it through. The
    // payload is forwarded unchanged — critical for actions like `setSpeakerModel`
    // that take primitive string payloads. Cleanup runs regardless so the modal closes
    // even if the re-dispatch throws (defensive — the action shape should be stable).
    if (pendingAction) {
      dispatch({
        type: pendingAction.type,
        payload: pendingAction.payload,
        meta: { _confirmedReset: true },
      } as unknown as UnknownAction);
    }
    cleanup();
  };

  return (
    <ConfirmDialog
      open={isOpen}
      type="warning"
      title="Manual edits will be reset"
      message="You have manually edited device positions in this design. Changing this setting will reset all device placements to the recommended solution. Do you want to continue?"
      confirmText="Discard edits & continue"
      cancelText="Keep edits"
      onConfirm={onConfirm}
      onCancel={cleanup}
    />
  );
};

export default ManualEditResetDialog;
